import { useEffect, useState } from "react";
import api from "@/api";
import { CheckCircle2, AlertTriangle, CircleDashed } from "lucide-react";

const STATUS_META = {
  validado: { label: "Validado", color: "#166534", bg: "#F0FDF4", Icon: CheckCircle2 },
  observaciones: { label: "Con observaciones", color: "#92400E", bg: "#FFFBEB", Icon: AlertTriangle },
  pendiente: { label: "Pendiente", color: "#6B7280", bg: "#F9FAFB", Icon: CircleDashed },
};
const CATEGORY_LABEL = { ciberseguridad: "Ciberseguridad", datos: "Datos y respaldos", integraciones: "Integraciones y dominio" };

export const ITChecklist = ({ canEdit }) => {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({}); // {id: comment}
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");

  const load = () => api.get("/it-checklist").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-sm text-[var(--ink-soft)]">Cargando checklist…</div>;

  const setStatus = async (item, status) => {
    setBusy(item.id); setErr("");
    try {
      await api.put(`/it-checklist/${item.id}`, {
        status,
        comment: drafts[item.id] !== undefined ? drafts[item.id] : item.comment,
      });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  const pct = data.total ? Math.round((data.counts.validado / data.total) * 100) : 0;
  const byCat = {};
  data.items.forEach((it) => { (byCat[it.category] = byCat[it.category] || []).push(it); });

  return (
    <div data-testid="it-checklist">
      {/* Progreso */}
      <div className="sketch-border bg-white p-4 mb-5 flex flex-wrap items-center gap-4" data-testid="itc-progress">
        <div className="flex-1 min-w-[220px]">
          <div className="h-3 rounded-full bg-[var(--cream-deep)] overflow-hidden">
            <div className="h-3 rounded-full bg-[#166534] transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="text-sm font-bold" data-testid="itc-pct">{pct}% validado</div>
        <div className="flex gap-2 text-xs">
          {Object.entries(STATUS_META).map(([k, m]) => (
            <span key={k} className="px-2 py-0.5 rounded-full font-bold" style={{ color: m.color, background: m.bg }}
              data-testid={`itc-count-${k}`}>
              {m.label}: {data.counts[k] || 0}
            </span>
          ))}
        </div>
      </div>

      {err && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="itc-error">{err}</div>}

      {Object.entries(byCat).map(([cat, items]) => (
        <div key={cat} className="mb-6">
          <div className="text-[11px] uppercase tracking-widest font-bold text-[var(--lala-blue)] mb-2">
            {CATEGORY_LABEL[cat] || cat}
          </div>
          <div className="space-y-2">
            {items.map((it) => {
              const meta = STATUS_META[it.status] || STATUS_META.pendiente;
              return (
                <div key={it.id} className="sketch-border-soft bg-white p-4" data-testid={`itc-item-${it.key}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2">
                        <meta.Icon size={15} style={{ color: meta.color }} />
                        <span className="font-bold text-sm">{it.title}</span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ color: meta.color, background: meta.bg }}
                          data-testid={`itc-status-${it.key}`}>{meta.label}</span>
                      </div>
                      <p className="text-xs text-[var(--ink-soft)] mt-1">{it.description}</p>
                      {it.comment && (
                        <p className="text-xs mt-1.5 italic text-[var(--ink)]" data-testid={`itc-comment-${it.key}`}>
                          “{it.comment}”
                        </p>
                      )}
                      {it.updated_by && (
                        <p className="text-[10px] text-[var(--ink-soft)] mt-1" data-testid={`itc-meta-${it.key}`}>
                          {it.updated_by} · {new Date(it.updated_at).toLocaleString("es-MX")}
                        </p>
                      )}
                    </div>
                    {canEdit && (
                      <div className="shrink-0 flex flex-col gap-2 w-full sm:w-[300px]">
                        <input type="text" placeholder="Comentario de TI (opcional)"
                          value={drafts[it.id] !== undefined ? drafts[it.id] : (it.comment || "")}
                          onChange={(e) => setDrafts((p) => ({ ...p, [it.id]: e.target.value }))}
                          className="input-field w-full text-xs" data-testid={`itc-input-${it.key}`} />
                        <div className="flex gap-1.5">
                          {Object.entries(STATUS_META).map(([k, m]) => (
                            <button key={k} disabled={busy === it.id} onClick={() => setStatus(it, k)}
                              className={`text-[11px] px-2.5 py-1 rounded-full font-bold border transition-colors disabled:opacity-40 ${it.status === k ? "text-white" : ""}`}
                              style={it.status === k ? { background: m.color, borderColor: m.color }
                                : { color: m.color, borderColor: m.color, background: "white" }}
                              data-testid={`itc-set-${k}-${it.key}`}>
                              {m.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ITChecklist;
